import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  handleLoginSignUpModal,
  LOGINMODAL,
} from "../../Redux/actions/commonActions";
import PatientService from "../../Services/PatientService";
import medDouble from "../../assets/PrescriptionUpload/medicineDouble.png";
import truck from "../../assets/PrescriptionUpload/truck.png";
import presImage from "../../assets/PrescriptionUpload/prescription1.png";
import presConfirm from "../../assets/PrescriptionUpload/presConfirm.png";
import { message } from "antd";
import { FiX, FiUploadCloud, FiFileText } from "react-icons/fi";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { FaRegTrashAlt } from "react-icons/fa";

const PrescriptionUpload = forwardRef(({ hideCard = false }, ref) => {
  const [isUploading, setIsUploading] = useState(false);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [file, setFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewURL, setPreviewURL] = useState("");
  
  const fileInputRef = useRef(null);
  const { patientData } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const MAX_SIZE = 10 * 1024 * 1024;

  const handleFileChange = (e) => {
    const selected = e.target.files && e.target.files[0];
    if (!selected) return;

    if (selected.size > MAX_SIZE) {
      message.warning("File exceeds the 10 MB limit.");
      return;
    }

    setFile(selected);
    setOpenUploadModal(true); // Open the modal once the file is loaded
    
    // Clear out input value to allow re-uploading the same file if desired
    if (e.target) {
      e.target.value = "";
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const selected = e.dataTransfer.files && e.dataTransfer.files[0];
    if (!selected) return;

    if (selected.size > MAX_SIZE) {
      message.warning("File exceeds the 10 MB limit.");
      return;
    }

    setFile(selected);
  };

  const handleOpenUploadModal = () => {
    if (!patientData || !patientData?.patientId) {
      dispatch(handleLoginSignUpModal({ data: true, actionType: LOGINMODAL }));
      return;
    }
    // Directly trigger file selection dialog on button click
    fileInputRef.current?.click();
  };

  useImperativeHandle(ref, () => ({
    open: handleOpenUploadModal,
  }));

  const handleOpenPreview = (fileToPreview) => {
    const url = URL.createObjectURL(fileToPreview);
    setPreviewURL(url);
    setPreviewFile(fileToPreview);
  };

  const handleClosePreview = () => {
    if (previewURL) {
      URL.revokeObjectURL(previewURL);
    }
    setPreviewURL("");
    setPreviewFile(null);
  };

  const handleCloseModal = () => {
    if (isUploading) return;
    handleClosePreview();
    setFile(null);
    setOpenUploadModal(false);
  };

  const handleSendOrder = async () => {
    if (!patientData || !patientData?.patientId) {
      dispatch(handleLoginSignUpModal({ data: true, actionType: LOGINMODAL }));
      return;
    }

    if (!file) {
      message.warning("Please upload a prescription before sending the order.");
      return;
    }

    setIsUploading(true);

    try {
      const toBase64 = (f) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onerror = () => reject(new Error("File reading failed"));
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(f);
        });

      const dataUrl = await toBase64(file);
      const base64 = dataUrl.split(",")[1] || "";

      let ext = "";
      if (file.type) {
        const parts = file.type.split("/");
        ext = parts[1] || "";
      }
      if (!ext && file.name) {
        const nameParts = file.name.split(".");
        ext = nameParts.length > 1 ? nameParts.pop() : "";
      }

      const payload = {
        firstName: patientData?.firstName || "",
        lastName: patientData?.lastName || "",
        createdBy: patientData?.createdBy || "DOXADMIN",
        modifiedBy: patientData?.modifiedBy || "DOXADMIN",
        patientId: patientData?.patientId,
        gender: patientData?.gender || "",
        mobile: patientData?.mobile || "",
        status: 1,
        patientType: "E",
        prescriptionImage: base64,
        prescriptionType: ext,
        createdDate: new Date().toISOString().slice(0, 19).replace("T", " "),
        modifiedDate: new Date().toISOString().slice(0, 19).replace("T", " "),
      };

      await PatientService.SendPrescriptionFile(payload);

      setFile(null);
      setOpenUploadModal(false);
      message.success(
        "Prescription sent successfully! We will contact you soon."
      );
    } catch (err) {
      console.error(err);
      message.error("Failed to prepare the order. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={hideCard ? "contents" : "w-full h-fit rounded-2xl bg-gradient-to-b from-[#189FAA] via-[#FFFFFF] to-[#4FB3B3] p-5 text-center shadow-lg sticky top-24"}>
      {!hideCard && (
        <>
      <h2 className="text-teal-800 font-semibold text-lg leading-snug">
        Upload Prescription and <br /> Get Medicine...
      </h2>

      <div className="flex justify-center mt-2 mb-4 text-5xl">
        <img src={medDouble} alt="" className="h-16 object-contain" />
      </div>

      <div className="flex flex-col items-center">
        <div className="w-16 h-16 flex items-center justify-center rounded-full border-2 border-teal-400 bg-white text-2xl text-teal-600 shadow">
          <img src={presImage} alt="" />
        </div>
        <p className="mt-3 text-sm text-gray-700 font-medium">
          Upload Your <br /> Prescription
        </p>
      </div>

      <div className="my-2 text-teal-500 text-xl">↓</div>

      <div className="flex flex-col items-center">
        <div className="w-16 h-16 flex items-center justify-center rounded-full border-2 border-teal-400 bg-white text-2xl text-teal-600 shadow">
          <img src={presConfirm} alt="" />
        </div>
        <p className="mt-3 text-sm text-gray-700 font-medium">
          We Confirm <br /> & Process It
        </p>
      </div>

      <div className="my-2 text-teal-500 text-xl">↓</div>

      <div className="flex flex-col items-center">
        <div className="w-16 h-16 flex items-center justify-center rounded-full border-2 border-teal-400 bg-white text-2xl text-teal-600 shadow">
          <img src={truck} alt="" />
        </div>
        <p className="mt-3 text-sm text-gray-700 font-medium">
          Receive Medicine <br /> at Your Door
        </p>
      </div>

      <button
        className="mt-4 w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 rounded-lg transition duration-300 shadow-md text-sm font-sans"
        onClick={handleOpenUploadModal}
      >
        Upload Prescription
      </button>
        </>
      )}

      {/* Hidden input to receive clicked trigger */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg"
        className="hidden"
        onChange={handleFileChange}
      />

      {openUploadModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-5">
          <div className="w-full max-w-sm bg-white rounded-3xl overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="px-7 py-3 flex justify-end items-center border-b">
              <button
                onClick={handleCloseModal}
                className="text-xl text-slate-500 hover:text-red-500 transition"
                disabled={isUploading}
              >
                <FiX />
              </button>
            </div>

            <div className="p-3 overflow-y-auto max-h-[80vh] scrollWidth">
              {/* Upload Area */}
              <h3 className="text-sm font-sans font-bold text-[#0C9BA4] mb-4">
                Upload your prescription, and we'll review it to ensure the right medicines are prepared and delivered to you
              </h3>

              <div
                className="border-2 border-dashed border-slate-200 rounded-2xl p-5 text-center flex flex-col items-center"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <div className="flex justify-center">
                  <div className="w-10 h-10 rounded-full bg-cyan-50 flex items-center justify-center">
                    <FiUploadCloud className="text-lg text-[#0C9BA4]" />
                  </div>
                </div>

                <h3 className="mt-3 text-sm font-sans font-semibold text-slate-700">
                  Drag & drop prescription here
                </h3>

                <label className="text-slate-400 text-sm font-sans">or</label>

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex text-sm font-sans w-fit cursor-pointer border border-[#0C9BA4] text-[#0C9BA4] font-semibold rounded-xl px-8 py-1 hover:bg-[#0C9BA4] hover:text-white transition"
                >
                  Choose File
                </button>

                <p className="mt-5 text-xs font-sans text-slate-400">JPG, PNG, PDF up to 10MB</p>
              </div>

              {/* Uploaded File details */}
              <div className="mt-8">
                <h3 className="text-sm font-sans font-bold text-slate-800">
                  Uploaded Prescription
                </h3>

                <div className="mt-5 space-y-4">
                  {!file ? (
                    <div className="border rounded-2xl p-5 text-center bg-slate-50">
                      <FiFileText className="mx-auto text-2xl text-slate-300" />
                      <p className="mt-4 text-slate-500 text-xs font-sans">
                        No prescription uploaded yet.
                      </p>
                    </div>
                  ) : (
                    (() => {
                      const isImage = file.type.startsWith("image");

                      return (
                        <div
                          className="border rounded-xl p-4 flex items-center justify-between hover:shadow-sm transition"
                        >
                          <div className="flex items-center gap-6">
                            {/* Preview */}
                            {isImage ? (
                              <img
                                src={URL.createObjectURL(file)}
                                alt=""
                                className="w-8 h-8 rounded-lg object-cover border"
                              />
                            ) : (
                              <div className="w-14 h-14 rounded-lg bg-red-50 flex items-center justify-center">
                                <FiFileText className="text-red-500 text-3xl" />
                              </div>
                            )}

                            {/* Details */}
                            <div>
                              <h4 className="font-semibold text-xs font-sans text-slate-800 break-all max-w-[120px]">
                                {file.name}
                              </h4>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            {/* View */}
                            <button
                              type="button"
                              onClick={() => handleOpenPreview(file)}
                              className="text-[#0C9BA4] font-semibold hover:underline"
                            >
                              <MdOutlineRemoveRedEye className="text-lg" />
                            </button>

                            {/* Delete */}
                            <button
                              type="button"
                              onClick={() => setFile(null)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <FaRegTrashAlt className="text-base" />
                            </button>
                          </div>
                        </div>
                      );
                    })()
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="mt-8 flex gap-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={isUploading}
                  className="flex-1 text-sm font-sans border border-[#0C9BA4] text-[#0C9BA4] font-semibold py-1 rounded-xl hover:bg-cyan-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleSendOrder}
                  disabled={isUploading}
                  className="flex-1 text-sm font-sans bg-gradient-to-r from-[#0C9BA4] to-[#11B7A9] text-white font-semibold py-1 rounded-xl shadow hover:opacity-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? "Uploading..." : "Confirm Order"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewFile && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-5">
          <div className="w-full max-w-2xl bg-white rounded-3xl overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 flex justify-between items-center border-b border-slate-100">
              <h3 className="text-sm font-sans font-bold text-slate-800 truncate max-w-[80%]">
                Preview: {previewFile.name}
              </h3>
              <button
                type="button"
                onClick={handleClosePreview}
                className="text-xl text-slate-500 hover:text-red-500 transition"
              >
                <FiX />
              </button>
            </div>

            {/* Content preview container */}
            <div className="p-4 bg-slate-50 flex items-center justify-center max-h-[70vh] min-h-[300px] overflow-auto">
              {previewFile.type.startsWith("image/") ? (
                <img
                  src={previewURL}
                  alt="prescription preview"
                  className="max-h-[60vh] max-w-full object-contain rounded-lg border border-slate-200 shadow-sm"
                />
              ) : (
                <iframe
                  title="prescription preview"
                  src={previewURL}
                  className="w-full h-[60vh] border border-slate-200 rounded-lg shadow-sm"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default PrescriptionUpload;
